// app-api 能源 API 的原始形狀（WP-6，~/work/handoff/2026-10-21-resume/energy-wp6-api.md；規格寫回 plan SPEC #energy-api「實作」段）→ 畫面形狀（types.ts）。
// 只轉真的回應；mock（mockData.ts）直接回畫面形狀。差別：
//   - 金額是 ISO 4217 最小幣值整數 amount_minor / cost_minor（TWD 2 位：NT$365 = 36500）→ 畫面用主單位 amount
//   - 不比較的原因叫 comparison_reason，值 coverage / meters_changed / no_prev；比較是 {pct, prev_mwh, diff_mwh}（上一期 < 1 度時 pct = null，看 diff_mwh）
//   - 時間格叫 slots（in_progress），排行的列沒有 can_control / power_mode（WP-6 單位 4），依設備還沒有（一律依孔）
//   - 電價單價在 versions[].periods[].price_per_kwh_e4（萬分之一元），方案套用的家戶叫 home_ids
//   - attention[]、show_carbon、names 還沒有：names 從 by_home[].name / tz、排行的 home_name 組
import type {
  Carbon,
  Comparison,
  DeviceEnergy,
  EnergySettings,
  EnergySummary,
  HomeRow,
  MeterKind,
  MonthReport,
  Money,
  NoCompareReason,
  OrgRow,
  Quality,
  RankPage,
  RankRow,
  SeriesPoint,
  Tariff,
  TariffKind,
  TariffRates,
  TariffTemplate,
} from './types';

/** 幣別的小數位數（ISO 4217 minor unit）。 */
const MINOR: Record<string, number> = { TWD: 2, CNY: 2, USD: 2, EUR: 2, JPY: 0, KRW: 0, HKD: 2 };
const minorDigits = (cur: string) => MINOR[cur] ?? 2;

export interface WireMoney {
  currency: string;
  amount_minor: number;
  homes?: number;
}

export function moneyOf(m: WireMoney): Money & { homes?: number } {
  return { currency: m.currency, amount: m.amount_minor / Math.pow(10, minorDigits(m.currency)), ...(m.homes !== undefined ? { homes: m.homes } : {}) };
}

/** 畫面金額 → 最小幣值整數（送回平台時）。 */
export function minorOf(currency: string, amount: number): number {
  return Math.round(amount * Math.pow(10, minorDigits(currency)));
}

export type WireReason = 'coverage' | 'meters_changed' | 'no_prev' | null | undefined;

export function reasonOf(r: WireReason): NoCompareReason | undefined {
  return r === 'coverage' ? 'prev_coverage' : r === 'meters_changed' ? 'meters_changed' : r === 'no_prev' ? 'no_prev' : undefined;
}

interface WireCmp {
  prev_mwh: number;
  diff_mwh?: number;
  pct?: number | null;
  meters?: number;
  complete?: boolean;
  prev_from?: string;
  prev_to?: string;
  excluded?: { home_id: string; reason: string; prev_coverage?: number }[];
}

function rowCmp(c: WireCmp | null | undefined): { ratio: number | null; prev_mwh: number } | null {
  if (!c) return null;
  return { ratio: c.pct === null || c.pct === undefined ? null : c.pct / 100, prev_mwh: c.prev_mwh };
}

interface WireSlot {
  t: string;
  date?: string;
  mwh: number | null;
  cost_minor?: number | null;
  quality: Quality;
  coverage?: number;
  in_progress?: boolean;
  tou?: string;
  avg_mw?: number | null;
  max_mw?: number | null;
}

export function slotOf(s: WireSlot): SeriesPoint {
  return { t: s.t, mwh: s.mwh ?? null, avg_mw: s.avg_mw ?? null, max_mw: s.max_mw ?? null, quality: s.quality, coverage: s.coverage ?? (s.mwh === null ? 0 : 1), partial: !!s.in_progress };
}

interface WireCarbon {
  kg: number;
  gas: 'co2e' | 'co2';
  factor_year: number;
  provisional: boolean;
  region: string;
  source_url?: string;
  homes?: number;
}

/** 地區代碼的顯示名（平台只給代碼）。 */
const REGION: Record<string, string> = { TW: '台灣', CN: '中國', JP: '日本', US: '美國' };

function carbonOf(c: WireCarbon): Carbon & { homes: number } {
  return { kg: c.kg, gas: c.gas, factor_year: c.factor_year, provisional: c.provisional, region: REGION[c.region] ?? c.region, homes: c.homes ?? 0 };
}

interface WireHomeRow {
  home_id: string;
  org_id?: string;
  site_id?: string;
  name?: string;
  tz?: string;
  mwh: number | null;
  comparison?: WireCmp | null;
  comparison_reason?: WireReason;
  daily_avg_mwh?: number | null;
  cost_by_currency?: WireMoney[];
  no_tariff?: boolean;
  carbon?: WireCarbon[];
  meters?: number;
  unavailable_meters?: number;
  incomplete_hours?: number;
  coverage?: number;
  quality?: Quality;
  top?: WireRankRow[];
}

function homeRowOf(r: WireHomeRow): HomeRow & { top?: RankRow[] } {
  return {
    home_id: r.home_id,
    org_id: r.org_id,
    mwh: r.mwh ?? 0,
    comparison: rowCmp(r.comparison),
    no_compare_reason: reasonOf(r.comparison_reason) ?? (r.comparison ? undefined : 'no_prev'),
    prev_coverage: r.comparison?.excluded?.[0]?.prev_coverage,
    daily_avg_mwh: r.daily_avg_mwh ?? 0,
    costs: (r.cost_by_currency ?? []).map(moneyOf),
    no_tariff: r.no_tariff,
    carbon: (r.carbon ?? []).map(carbonOf),
    meters: r.meters ?? 0,
    unavailable_meters: r.unavailable_meters ?? 0,
    incomplete_hours: r.incomplete_hours ?? 0,
    unavailable: r.mwh === null && (r.meters ?? 0) > 0,
    ...(r.top ? { top: r.top.map((x) => rankRowOf(x)) } : {}),
  };
}

interface WireOrgRow {
  org_id: string;
  homes: number;
  homes_with_meters: number;
  mwh: number | null;
  meters: number;
  unavailable_meters: number;
  incomplete_hours: number;
  no_tariff_homes: number;
  cost_by_currency: WireMoney[];
  comparison?: WireCmp | null;
  comparison_reason?: WireReason;
  daily_avg_mwh?: number | null;
}

function orgRowOf(r: WireOrgRow): OrgRow {
  return {
    org_id: r.org_id,
    homes: r.homes,
    homes_with_meters: r.homes_with_meters,
    mwh: r.mwh ?? 0,
    comparison: rowCmp(r.comparison),
    no_compare_reason: reasonOf(r.comparison_reason) ?? (r.comparison ? undefined : 'no_prev'),
    daily_avg_mwh: r.daily_avg_mwh ?? 0,
    costs: (r.cost_by_currency ?? []).map(moneyOf),
    no_tariff_homes: r.no_tariff_homes,
    meters: r.meters,
    unavailable_meters: r.unavailable_meters,
    incomplete_hours: r.incomplete_hours,
  };
}

export interface WireOrgSummary {
  period: EnergySummary['period'];
  from: string;
  to: string;
  mwh: number | null;
  forecast_mwh?: number | null;
  cost_by_currency: WireMoney[];
  carbon?: WireCarbon[];
  coverage: number;
  quality: Quality;
  incomplete_hours: number;
  meters: number;
  poe_meters?: number;
  incomplete_meters?: number;
  homes_with_meters: number;
  visible_homes: number;
  total_homes: number;
  unavailable_homes?: (string | { home_id: string; since?: string })[];
  homes_without_tariff?: string[];
  comparison?: WireCmp | null;
  comparison_reason?: WireReason;
  series?: WireSlot[];
  mixed_tz?: boolean;
  tz?: string;
  by_home?: WireHomeRow[];
  by_org?: WireOrgRow[];
  show_carbon?: boolean;
  attention?: EnergySummary['attention'];
  names?: EnergySummary['names'];
  [k: string]: unknown;
}

/** 已經是畫面形狀（mock）就原樣回。 */
function isView(x: unknown): boolean {
  const o = x as { cost_by_currency?: { amount?: number }[]; by_home?: { costs?: unknown }[] };
  return !!o && (o.cost_by_currency?.some((c) => c.amount !== undefined) || o.by_home?.some((h) => h.costs !== undefined) || false);
}

export function summaryOf(w: WireOrgSummary): EnergySummary {
  if (isView(w)) return w as unknown as EnergySummary;
  const byHome = (w.by_home ?? []).map(homeRowOf);
  const homes = Object.fromEntries((w.by_home ?? []).map((h) => [h.home_id, { name: h.name ?? h.home_id, tz: h.tz, org_id: h.org_id }]));
  const c = w.comparison;
  const comparison: Comparison | null = c
    ? {
        prev_mwh: c.prev_mwh,
        cur_mwh: c.prev_mwh + (c.diff_mwh ?? 0),
        ratio: c.pct === null || c.pct === undefined ? null : c.pct / 100,
        meters: c.meters ?? w.meters,
        homes: Math.max(0, w.homes_with_meters - (c.excluded?.length ?? 0)),
        prev_from: c.prev_from ?? '',
        prev_to: c.prev_to ?? '',
        excluded: (c.excluded ?? []).map((x) => ({ home_id: x.home_id, reason: reasonOf(x.reason as WireReason) ?? 'no_prev', prev_coverage: x.prev_coverage })),
      }
    : null;
  return {
    period: w.period,
    from: w.from,
    to: w.to,
    mwh: w.mwh ?? 0,
    forecast_mwh: w.forecast_mwh ?? null,
    cost_by_currency: w.cost_by_currency.map((m) => ({ ...moneyOf(m), homes: m.homes ?? 0 })),
    carbon: (w.carbon ?? []).map(carbonOf),
    coverage: w.coverage,
    quality: w.quality,
    incomplete_hours: w.incomplete_hours,
    incomplete_meters: w.incomplete_meters ?? 0,
    meters: w.meters,
    poe_meters: w.poe_meters ?? 0,
    comparison,
    no_compare_reason: reasonOf(w.comparison_reason) ?? (comparison ? undefined : 'no_prev'),
    homes_without_tariff: w.homes_without_tariff ?? [],
    unavailable_homes: (w.unavailable_homes ?? []).map((x) => (typeof x === 'string' ? { home_id: x } : x)),
    visible_homes: w.visible_homes,
    total_homes: w.total_homes,
    homes_with_meters: w.homes_with_meters,
    mixed_tz: w.mixed_tz,
    tz: w.tz,
    series: (w.series ?? []).map(slotOf),
    attention: w.attention ?? [],
    by_home: byHome,
    by_org: w.by_org?.map(orgRowOf),
    show_carbon: w.show_carbon,
    names: { homes: { ...homes, ...(w.names?.homes ?? {}) }, orgs: w.names?.orgs ?? {}, devices: w.names?.devices ?? {} },
  };
}

interface WireRankRow {
  meter: string;
  kind: MeterKind;
  home_id: string;
  org_id?: string;
  home_name?: string;
  name: string;
  device_ref?: string;
  station_ref?: string;
  station_name?: string;
  port?: number;
  poe_port?: number | null;
  mwh: number | null;
  avg_mw: number | null;
  max_mw: number | null;
  cost_by_currency?: WireMoney[];
  comparison?: WireCmp | null;
  comparison_reason?: WireReason;
  quality: Quality;
  incomplete_hours?: number;
  coverage?: number;
  can_control?: boolean;
  power_mode?: number;
  power_mode_options?: number[];
}

export function rankRowOf(r: WireRankRow | RankRow): RankRow {
  if ((r as RankRow).costs !== undefined) return r as RankRow;
  const w = r as WireRankRow;
  return {
    meter: w.meter,
    kind: w.kind,
    home_id: w.home_id,
    org_id: w.org_id,
    name: w.name ?? '',
    station_ref: w.station_ref,
    station_name: w.station_name ?? (w.station_ref ? 'Station' : undefined),
    port: w.port ?? w.poe_port ?? undefined,
    device_ref: w.device_ref,
    power_mode: w.power_mode,
    power_mode_options: w.power_mode_options,
    can_control: w.can_control,
    mwh: w.mwh ?? 0,
    avg_mw: w.avg_mw,
    max_mw: w.max_mw,
    costs: (w.cost_by_currency ?? []).map(moneyOf),
    comparison: rowCmp(w.comparison),
    no_compare_reason: reasonOf(w.comparison_reason),
    quality: w.quality,
    incomplete_hours: w.incomplete_hours ?? 0,
    unavailable: w.mwh === null,
  };
}

export function rankPageOf(w: { items: (WireRankRow | RankRow)[]; next_cursor?: string | null; total: number; names?: EnergySummary['names'] }): RankPage & { names?: EnergySummary['names'] } {
  const homes: Record<string, { name: string }> = {};
  for (const it of w.items as WireRankRow[]) if (it.home_name) homes[it.home_id] = { name: it.home_name };
  return { items: w.items.map(rankRowOf), next_cursor: w.next_cursor ?? null, total: w.total, names: { ...(w.names ?? {}), homes: { ...homes, ...(w.names?.homes ?? {}) } } };
}

export function reportOf(w: WireOrgSummary & { month: string; finalized: boolean; finalize_on: string; summary?: unknown; by_home?: WireHomeRow[] }): MonthReport & { names?: EnergySummary['names'] } {
  if (w.summary) return w as unknown as MonthReport & { names?: EnergySummary['names'] };
  const s = summaryOf(w);
  return { month: w.month, finalized: w.finalized, finalize_on: w.finalize_on, summary: s, by_home: (w.by_home ?? []).map(homeRowOf), names: s.names };
}

interface WireHomeSummary {
  meters?: (WireRankRow & { share_pct?: number })[];
  meters_detail?: RankRow[];
  [k: string]: unknown;
}

/** 家戶摘要（報表展開、比較{leaf}）：meters[] → 量測點列。 */
export function homeSummaryOf(home: string, w: WireHomeSummary): EnergySummary & { home_id: string; meters_detail?: RankRow[] } {
  if (w.meters_detail) return w as unknown as EnergySummary & { home_id: string; meters_detail?: RankRow[] };
  const base = w as unknown as WireOrgSummary;
  const s = summaryOf({ ...base, by_home: base.by_home ?? [], homes_with_meters: base.homes_with_meters ?? 1, visible_homes: base.visible_homes ?? 1, total_homes: base.total_homes ?? 1, meters: Array.isArray(w.meters) ? w.meters.length : 0 });
  return { ...s, home_id: home, meters_detail: (w.meters ?? []).map((m) => rankRowOf({ ...m, home_id: home, kind: m.kind ?? 'plug' })) };
}

interface WireDevice {
  now?: { mw: number } | null;
  today_mwh?: number | null;
  month_mwh?: number | null;
  month_cost_by_currency?: WireMoney[];
  slots?: WireSlot[];
  tariff?: unknown;
  meters?: (WireRankRow & { poe_port?: number | null; device_ref?: string; upstream_meter?: string | null })[];
  recording_since?: string | null;
  [k: string]: unknown;
}

/** 單台（設備頁、Station 每孔）。 */
export function deviceOf(home: string, device: string, w: WireDevice | DeviceEnergy): DeviceEnergy {
  if ((w as DeviceEnergy).month_costs !== undefined) return w as DeviceEnergy;
  const d = w as WireDevice;
  const ports = (d.meters ?? []).filter((m) => m.poe_port !== null && m.poe_port !== undefined);
  return {
    device_ref: device,
    home_id: home,
    power_mw: d.now?.mw ?? null,
    today_mwh: d.today_mwh ?? null,
    month_mwh: d.month_mwh ?? null,
    month_costs: (d.month_cost_by_currency ?? []).map(moneyOf),
    no_tariff: d.tariff === null,
    ports: ports.length
      ? ports.map((p) => ({ port: p.poe_port!, meter: p.meter, today_mwh: p.mwh ?? null, spark: [], device_ref: p.device_ref !== device ? p.device_ref : undefined, name: p.name || undefined }))
      : undefined,
    series: (d.slots ?? []).map(slotOf),
    first_seen: d.recording_since ?? undefined,
  };
}

interface WirePeriod {
  season?: string;
  day_type?: string;
  from_hour: number;
  to_hour: number;
  label?: string;
  price_per_kwh_e4?: number;
}

interface WireTariffVersion {
  version: number;
  effective_from: string;
  kind: TariffKind | 'flat' | 'tiered' | 'tou';
  seasons?: { name?: string; id?: string; from?: string; to?: string; start?: string; end?: string }[];
  periods?: WirePeriod[];
  tiers?: { up_to_kwh: number | null; prices?: { summer?: number; nonsummer?: number }; price_per_kwh_e4?: number }[];
}

/** 版本 → 畫面的單價（看不到單價時 undefined）。 */
export function ratesOf(v: WireTariffVersion): TariffRates | undefined {
  const ps = v.periods ?? [];
  if (v.kind === 'tiered' && v.tiers?.length) {
    const ok = v.tiers.every((x) => x.price_per_kwh_e4 !== undefined || x.prices?.nonsummer !== undefined);
    if (!ok) return undefined;
    return { tiers: v.tiers.map((x) => ({ upto_kwh: x.up_to_kwh, price: x.price_per_kwh_e4 !== undefined ? x.price_per_kwh_e4 / 1e4 : (x.prices!.nonsummer as number) })) };
  }
  if (!ps.length || ps.some((p) => p.price_per_kwh_e4 === undefined)) return undefined;
  if (v.kind === 'flat') return { flat: ps[0].price_per_kwh_e4! / 1e4 };
  const seasons = new Map<string, NonNullable<TariffRates['seasons']>[number]>();
  for (const p of ps) {
    const key = p.season ?? '';
    const s = seasons.get(key) ?? { name: key, from: '', to: '', periods: [] };
    const days = p.day_type === 'weekday' ? 'weekday' : p.day_type === 'weekend' || p.day_type === 'holiday' ? 'weekend' : 'all';
    s.periods.push({ name: p.label ?? '', days, from_h: p.from_hour, to_h: p.to_hour, price: p.price_per_kwh_e4! / 1e4 });
    seasons.set(key, s);
  }
  return { seasons: [...seasons.values()] };
}

/** 畫面的單價 → 平台的 periods（萬分之一元整數）。 */
export function periodsOf(kind: TariffKind, r: TariffRates): WirePeriod[] {
  const e4 = (x: number) => Math.round(x * 1e4);
  if (kind === 'flat') return [{ from_hour: 0, to_hour: 24, day_type: 'all', price_per_kwh_e4: e4(r.flat ?? 0) }];
  return (r.seasons ?? []).flatMap((s) => s.periods.map((p) => ({ season: s.name, day_type: p.days, from_hour: p.from_h, to_hour: p.to_h, label: p.name, price_per_kwh_e4: e4(p.price) })));
}

interface WireTariff {
  tariff_id: string;
  owner?: string;
  org_id?: string;
  name: string;
  currency: string;
  template_id?: string;
  versions: WireTariffVersion[];
  home_ids?: string[];
  applied_homes?: string[];
}

export function tariffOf(org: string, w: WireTariff | Tariff): Tariff {
  if ((w as Tariff).applied_homes !== undefined && (w as Tariff).kind !== undefined) return w as Tariff;
  const t = w as WireTariff;
  const last = t.versions[t.versions.length - 1];
  return {
    tariff_id: t.tariff_id,
    org_id: t.org_id ?? org,
    name: t.name,
    kind: (last?.kind ?? 'flat') as TariffKind,
    currency: t.currency,
    template_id: t.template_id,
    versions: t.versions.map((v) => ({ version: v.version, effective_from: v.effective_from, rates: ratesOf(v) })),
    applied_homes: t.home_ids ?? t.applied_homes ?? [],
    version: last?.version,
  };
}

export function tariffListOf(org: string, w: { items?: (WireTariff | Tariff)[]; tariffs?: (WireTariff | Tariff)[] } | (WireTariff | Tariff)[]): { items: Tariff[] } {
  const list = Array.isArray(w) ? w : (w.items ?? w.tariffs ?? []);
  return { items: list.map((x) => tariffOf(org, x)) };
}

interface WireTemplate {
  id: string;
  region: string;
  name: Record<string, string> | string;
  currency: string;
  versions: WireTariffVersion[];
}

export function templatesOf(w: { templates?: WireTemplate[]; items?: TariffTemplate[] }, lang: string): { items: TariffTemplate[] } {
  if (w.items) return { items: w.items };
  const key = lang === 'zh-Hans' ? 'zh-CN' : lang === 'zh-Hant' ? 'zh-TW' : lang;
  return {
    items: (w.templates ?? []).map((x) => {
      const v = x.versions[x.versions.length - 1];
      const name = typeof x.name === 'string' ? x.name : (x.name[key] ?? x.name['zh-TW'] ?? Object.values(x.name)[0] ?? x.id);
      return { template_id: x.id, region: x.region, name, kind: (v?.kind ?? 'flat') as TariffKind, currency: x.currency, rates: (v && ratesOf(v)) ?? {}, source: undefined };
    }),
  };
}

interface WireSettings {
  tariff?: { tariff_id?: string; name?: string } | null;
  tariff_source?: 'home' | 'org' | null;
  tariff_id?: string | null;
  carbon_category?: string;
  alerts: EnergySettings['alerts'];
  managed_by_template?: string | null;
  can_edit?: boolean;
  kmgr_enabled?: boolean;
  version?: number;
}

export function settingsOf(home: string, w: WireSettings | EnergySettings): EnergySettings {
  if ((w as EnergySettings).home_id !== undefined) return w as EnergySettings;
  const s = w as WireSettings;
  return {
    home_id: home,
    tariff_id: s.tariff?.tariff_id ?? s.tariff_id ?? null,
    tariff_source: s.tariff_source ?? 'none',
    tariff_name: s.tariff?.name,
    carbon_category: s.carbon_category,
    alerts: s.alerts,
    managed_by_template: s.managed_by_template ?? null,
    kmgr_enabled: s.kmgr_enabled,
    can_edit: s.can_edit,
    version: s.version,
  };
}

/** 排行的排序鍵：平台只有 mwh / max_mw / cost（平均與比上期在畫面上排這一頁）。 */
export function rankSortOf(sort: string | undefined): string | undefined {
  const k = (sort ?? '').split(':')[0];
  return k === 'max' ? 'max_mw' : k === 'mwh' || !k ? 'mwh' : 'mwh';
}
